import type { NodeStatus } from '@occ/contracts';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchNodes, type NodeRow } from '../data/nodes';
import { useHierarchy } from '../hierarchy/HierarchyProvider';
import { cost, percent, timeAgo } from '../lib/format';
import {
  Alert,
  Badge,
  Card,
  DataTable,
  DataTableColumnHeader,
  Loading,
  Select,
  StatusDot,
} from '../components/ui';

const NODE_TYPE_LABEL: Record<string, string> = { vps: 'VPS', fisico: 'Físico' };

/** Fila con el estado y la ubicación en vivo ya fundidos con lo que trajo el backend. */
type DisplayRow = NodeRow & { liveStatus: NodeStatus; location: string };

/**
 * Lista de nodos con búsqueda por alias y filtro por estado (RF-07, RF-17).
 *
 * El filtrado ocurre en el backend —es el que pagina y el que sabe qué ve cada
 * usuario— pero el **estado** de cada fila se pisa con el que llega por el
 * canal en vivo. Así una lista abierta en una pared no envejece sin que nadie
 * la recargue.
 */

const SEARCH_DEBOUNCE_MS = 250;

/**
 * Fuera del componente: no depende de ningún estado, así que no hace falta
 * recrearla en cada render — `useReactTable` la recibe igual de estable.
 */
const COLUMNS: ColumnDef<DisplayRow>[] = [
  {
    accessorKey: 'alias',
    meta: { label: 'Alias' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Alias" />,
    cell: ({ row }) => (
      <Link
        to={`/nodes/${row.original.id}`}
        className="inline-flex items-center gap-2 hover:underline"
      >
        <StatusDot status={row.original.liveStatus} />
        {row.original.alias}
      </Link>
    ),
  },
  {
    accessorKey: 'location',
    meta: { label: 'Ubicación' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Ubicación" />,
    cell: ({ getValue }) => <span className="opacity-70">{getValue<string>()}</span>,
  },
  {
    accessorKey: 'ip',
    meta: { label: 'IP' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="IP" />,
    cell: ({ getValue }) => <span className="opacity-70">{getValue<string | null>() ?? '—'}</span>,
  },
  {
    accessorKey: 'nodeType',
    meta: { label: 'Tipo' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Tipo" />,
    cell: ({ getValue }) => {
      const value = getValue<string | null>();
      return <span className="opacity-70">{value ? (NODE_TYPE_LABEL[value] ?? value) : '—'}</span>;
    },
  },
  {
    accessorKey: 'cpuAvgPercent',
    meta: { label: 'CPU prom.' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="CPU prom." />,
    cell: ({ getValue }) => (
      <span className="opacity-70">{percent(getValue<number | null>())}</span>
    ),
  },
  {
    accessorKey: 'memAvgPercent',
    meta: { label: 'RAM prom.' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="RAM prom." />,
    cell: ({ getValue }) => (
      <span className="opacity-70">{percent(getValue<number | null>())}</span>
    ),
  },
  {
    accessorKey: 'provider',
    meta: { label: 'Provider' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Provider" />,
    cell: ({ getValue }) => <span className="opacity-70">{getValue<string | null>() ?? '—'}</span>,
  },
  {
    accessorKey: 'monthlyCostCents',
    meta: { label: 'Costo' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Costo" />,
    cell: ({ getValue }) => <span className="opacity-70">{cost(getValue<number | null>())}</span>,
  },
  {
    accessorKey: 'osFamily',
    meta: { label: 'Sistema' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Sistema" />,
    cell: ({ getValue }) => <span className="opacity-70">{getValue<string | null>() ?? '—'}</span>,
  },
  {
    accessorKey: 'agentVersion',
    meta: { label: 'Agente' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Agente" />,
    cell: ({ getValue }) => {
      const value = getValue<string | null>();
      return value ? <Badge>{value}</Badge> : <span className="opacity-70">—</span>;
    },
  },
  {
    accessorKey: 'lastSeenAt',
    meta: { label: 'Visto' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Visto" />,
    sortingFn: 'datetime',
    cell: ({ getValue }) => <span className="opacity-70">{timeAgo(getValue<Date | null>())}</span>,
  },
];

export function NodesPage() {
  const { nodes: liveNodes, tree, loading: treeLoading } = useHierarchy();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState<NodeStatus | ''>('');
  const [clientId, setClientId] = useState('');
  const [branchId, setBranchId] = useState('');

  // Elegir otro cliente sin resetear la sucursal dejaría filtrando por una
  // sucursal que no es de ese cliente, y la lista se vería vacía sin motivo
  // visible en pantalla.
  useEffect(() => setBranchId(''), [clientId]);

  const branchOptions = useMemo(
    () =>
      clientId ? (tree?.clients.find((client) => client.id === clientId)?.branches ?? []) : [],
    [tree, clientId],
  );

  const [rows, setRows] = useState<NodeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchNodes({
      search: debouncedSearch || undefined,
      status: status || undefined,
      clientId: clientId || undefined,
      branchId: branchId || undefined,
    })
      .then((result) => {
        if (cancelled) return;
        setRows(result);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(cause instanceof Error ? cause.message : 'No se pudieron listar los nodos');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, status, clientId, branchId]);

  /** Estado y ubicación vivos, indexados por nodo. */
  const live = useMemo(() => {
    const map = new Map<string, { status: NodeStatus; clientName: string; branchName: string }>();
    for (const node of liveNodes) {
      map.set(node.id, {
        status: node.status,
        clientName: node.clientName,
        branchName: node.branchName,
      });
    }
    return map;
  }, [liveNodes]);

  const displayRows = useMemo<DisplayRow[]>(
    () =>
      rows.map((node) => {
        const overlay = live.get(node.id);
        return {
          ...node,
          liveStatus: overlay?.status ?? node.status,
          location: overlay ? `${overlay.clientName} · ${overlay.branchName}` : '—',
        };
      }),
    [rows, live],
  );

  return (
    <section className="grid gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Nodos</h1>
          <p className="text-sm opacity-60">
            {rows.length} {rows.length === 1 ? 'nodo' : 'nodos'} dentro de tu alcance
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="grid gap-1 text-sm">
            <span className="opacity-70">Buscar</span>
            <input
              type="search"
              value={search}
              placeholder="Frutal-Centro-Caja1"
              onChange={(event) => setSearch(event.target.value)}
              className="rounded border border-black/15 dark:border-white/20 bg-transparent px-2.5 py-1.5 outline-none focus:border-[var(--occ-brand)]"
            />
          </label>
          <Select
            label="Estado"
            value={status}
            onChange={(event) => setStatus(event.target.value as NodeStatus | '')}
          >
            <option value="">Todos</option>
            <option value="online">En línea</option>
            <option value="offline">Desconectados</option>
            <option value="unknown">Desconocidos</option>
          </Select>
          <Select
            label="Cliente"
            value={clientId}
            onChange={(event) => setClientId(event.target.value)}
          >
            <option value="">Todos</option>
            {(tree?.clients ?? []).map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </Select>
          <Select
            label="Sucursal"
            value={branchId}
            disabled={!clientId}
            title={clientId ? undefined : 'Elige primero un cliente'}
            onChange={(event) => setBranchId(event.target.value)}
          >
            <option value="">Todas</option>
            {branchOptions.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {error && <Alert>{error}</Alert>}

      <Card>
        {(loading || treeLoading) && rows.length === 0 ? (
          <Loading label="Consultando nodos…" />
        ) : (
          <DataTable
            columns={COLUMNS}
            data={displayRows}
            emptyMessage="Ningún nodo coincide con el filtro."
          />
        )}
      </Card>
    </section>
  );
}
